import classNames from 'classnames';
import React from 'react';

import { SidebarCollapseIcon } from '@components/Icons/SidebarCollapseIcon';
import { InternalTestingBadge } from '@components/InternalTestingBadge/InternalTestingBadge';
import { openAuthedChatUrl } from '@lib/network/chats';
import AddIcon from '@mui/icons-material/Add';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import { Button, ButtonProps, Drawer, Tooltip } from '@mui/material';
import { MOMENTS_HEADER } from '@pages/AppPage';
import { Logo } from '@pages/Auth/Logo';
import * as Sentry from '@sentry/react';
import { debugStore } from '@stores/Debug';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react';
import { useLocation, useNavigate } from 'react-router';
import { AccountFooter } from './AccountFooter/AccountFooter';
import { ChangelogDot } from './ChangelogDot';
import css from './Nav.module.scss';
import { NAV_ICONS } from './navIcons';
import { NAV_ITEMS, NavItemBadge, NavItemHome, NavItemId, SharedNavItem } from './navItems.shared';
import { RecentChatsSection } from './RecentChatsSection/RecentChatsSection';
import { useNavActions } from './useNavActions';

/** Which app this build represents. Drives internal-vs-external routing. */
const THIS_APP: NavItemHome = 'starzero';

/** Origin of the other app, used when an item's `home` doesn't match THIS_APP. */
const CHAT_URL = process.env.CHAT_URL?.replace(/\/$/, '') ?? '';
const EXTERNAL_HOSTS: Record<NavItemHome, string> = {
  starzero: '',
  vibedit: CHAT_URL,
};

/**
 * Local grouping of the (flat, mirrored) shared nav items into labeled sections.
 * `navItems.shared.ts` stays flat because it's byte-mirrored across repos; the
 * sectioning is a presentation concern that lives here.
 *
 * The nav is flat (no tab switcher): a headerless primary group, then a
 * "PRODUCTS" group for the named agent tools. Workspace is intentionally NOT a
 * row — the logo carries the Workspace home destination. `showChatActions`
 * wraps the primary group with the chat-app rows — New Chat + Chats before the
 * shared items, Artifacts after (they're local actions, not shared nav items).
 */
const NAV_SECTIONS: {
  label?: string;
  ids: NavItemId[];
  showChatActions?: boolean;
  /** Shared items rendered after the chat-app rows (Artifacts), which trail `ids`. */
  trailingIds?: NavItemId[];
}[] = [
  { ids: ['search', 'libraries', 'customize'], showChatActions: true, trailingIds: ['schedule'] },
  { label: 'Products', ids: ['podcasts'] },
];

/**
 * Per-item display-label overrides. "Search" reads as "search everything" once a
 * Chats item sits near it, so the flagship moment search is labelled "Moments"
 * (its results are moments) to break that collision. The shared `navItems.shared.ts`
 * keeps the neutral "Search" label (and analytics keep tracking "Search"); only the
 * rendered copy changes here.
 */
const NAV_LABEL_OVERRIDES: Partial<Record<NavItemId, string>> = {
  search: MOMENTS_HEADER,
};
const NAV_ITEMS_BY_ID = Object.fromEntries(NAV_ITEMS.map((item) => [item.id, item])) as Record<
  NavItemId,
  SharedNavItem
>;

const BADGE_CLASS_MAP: Record<NavItemBadge, string> = {
  BETA: css.betaBadge,
  NEW: css.newBadge,
};

function renderStaticBadge(badge: NavItemBadge | undefined): React.ReactNode {
  if (!badge) return undefined;
  return <span className={BADGE_CLASS_MAP[badge]}>{badge}</span>;
}

// One Sentry alert per session is enough — a missing CHAT_URL is a build /
// env-config problem, not a per-click symptom.
let missingChatUrlReported = false;
function reportMissingChatUrl(item: SharedNavItem) {
  const message = `[Nav] CHAT_URL is not configured — cannot navigate to "${item.label}" (${item.home}${item.path})`;
  console.warn(message);
  if (missingChatUrlReported) return;
  missingChatUrlReported = true;
  Sentry.captureMessage(message, {
    level: 'error',
    tags: { nav: 'missing-chat-url', itemId: item.id, itemHome: item.home },
  });
}

// Collapsed state persisted so the rail stays as the user left it across reloads.
// localStorage rather than `UIPreferencesStore`: this value is read during the FIRST
// layout pass (see the `--nav-drawer-width` effect below, which is `useLayoutEffect`
// for exactly that reason), and UIPreferences hydrates asynchronously (`isLoading`) —
// sourcing it there would paint one frame at the open width before snapping to the rail.
// Mirrors `RecentChatsSection`'s `nav.chatSections.collapsed` helpers.
const DRAWER_STORAGE_KEY = 'nav.drawer.opened';

function readOpened(): boolean {
  try {
    const raw = localStorage.getItem(DRAWER_STORAGE_KEY);
    return raw === null ? true : raw === 'true';
  } catch {
    return true; // unavailable storage — default to expanded
  }
}

function persistOpened(next: boolean): boolean {
  try {
    localStorage.setItem(DRAWER_STORAGE_KEY, String(next));
  } catch {
    // ignore unavailable storage — collapse state just won't persist
  }
  return next;
}

type DesktopNavProps = {
  /** Rendered inside the mobile overlay instead of occupying page layout. */
  floating?: boolean;
  /** Closes the mobile overlay when its in-sidebar close control is used. */
  onFloatingClose?: () => void;
};

export const DesktopNav = observer(function DesktopNav({
  floating = false,
  onFloatingClose,
}: DesktopNavProps) {
  // Seeded from storage, except in the mobile overlay: `MobileNav` renders this same
  // component with `floating`, and a desktop user who left the rail collapsed must not
  // open the overlay onto a 60px icon strip. `floating` never changes for a given mount,
  // so reading it in the initializer is safe.
  const [openedDrawer, setOpenedDrawer] = React.useState(() => (floating ? true : readOpened()));
  const libraryManager = useLibraryManager();

  const nav = useNavigate();
  const currentPath = useLocation().pathname;

  const { navigateToSearch, navigateToNewChat, navigateToChats, navigateToArtifacts } =
    useNavActions();

  // Close the mobile overlay on every nav tap. MobileNav also closes on route
  // change (its pathname effect), but that misses same-route / query-only taps
  // (e.g. tapping the current route's item, or Search's `?libs=` re-scope), so
  // the explicit close stays here; the pathname effect is the catch-all for
  // programmatic navigations (logout, etc.).
  const handleNavItemClick = React.useCallback(
    (item: SharedNavItem) => {
      // Items with bespoke local behaviour (query-param prefill, etc.) keep their
      // own action hooks. Everything else uses the default below.
      if (item.id === 'search') {
        navigateToSearch();
        onFloatingClose?.();
        return;
      }

      trackingStore.navItemClicked(item.label, item.path);
      if (item.home === THIS_APP) {
        nav(item.path);
        onFloatingClose?.();
        return;
      }
      const host = EXTERNAL_HOSTS[item.home];
      if (host) {
        // Close the overlay before the (async, fallible) token exchange:
        // on success the page unloads anyway; on failure the user must not
        // be left stranded under the open overlay with only an error toast.
        onFloatingClose?.();
        // Attach a short-lived exchange token so the chat app doesn't
        // bounce back to app.starzero.ai for a handoff.
        void openAuthedChatUrl(`${host}${item.path}`, '_self', {
          surface: 'sidebar',
          // Keyed on the stable `id` ('podcasts', 'customize'), not the label:
          // labels are display copy and get re-worded (see NAV_LABEL_OVERRIDES),
          // which would silently split this dimension in the dashboard.
          control: `nav-item:${item.id}`,
        });
      } else {
        reportMissingChatUrl(item);
      }
    },
    [nav, navigateToSearch, onFloatingClose],
  );

  // The logo is the Workspace home entry point — Workspace is no longer a nav row.
  // Drive it from the shared nav item so the destination/label stay in one place.
  const handleLogoClick = React.useCallback(() => {
    const workspace = NAV_ITEMS_BY_ID.workspace;
    trackingStore.navItemClicked(workspace.label, workspace.path);
    nav(workspace.path);
    onFloatingClose?.();
  }, [nav, onFloatingClose]);

  const toggleDrawer = React.useCallback(() => {
    if (floating) {
      onFloatingClose?.();
      return;
    }
    // Only the desktop rail reaches this line (the `floating` early-return above), so
    // the overlay can never write a collapsed value the desktop would later read.
    setOpenedDrawer((open) => persistOpened(!open));
  }, [floating, onFloatingClose]);

  // Per-item renderer, shared by the section groups. Applies the display-label
  // override ("Search" → "Moments"), any static badge, and the collapsed tooltip.
  // Schedule is the publishing entry point: team-only while publishing is in
  // internal testing, badged as such (see `FIXME(publishing-internal-only)`).
  const renderNavItem = (item: SharedNavItem) => {
    const publishing = item.id === 'schedule';
    if (publishing && !debugStore.showPublishingEntryPoints) return null;
    const isActive = item.home === THIS_APP && currentPath === item.path;
    const label = NAV_LABEL_OVERRIDES[item.id] ?? item.label;
    return (
      <NavButton
        key={item.id}
        tooltip={!openedDrawer ? label : undefined}
        label={label}
        activeWhenPathIs={isActive}
        openedDrawer={openedDrawer}
        icon={NAV_ICONS[item.iconKey]}
        badge={
          publishing && debugStore.publishingInternalOnly ? (
            <InternalTestingBadge className={css.internalBadge} />
          ) : (
            renderStaticBadge(item.badge)
          )
        }
        onClick={() => handleNavItemClick(item)}
      />
    );
  };

  // Desktop rail owns the inline --nav-drawer-width; on mobile the overlay
  // (floating) reserves no page width and the value comes from root.scss's
  // `@include mobile` floor (0). Keep these in sync with Nav.module.scss
  // $open-width / $close-width — this var drives the main panes'
  // `max-width: calc(100dvw - var(--nav-drawer-width))`.
  //
  // useLayoutEffect (not useEffect) so the inline write/clear lands BEFORE paint:
  // an inline documentElement style outranks the stylesheet floor, so on a
  // desktop→mobile resize the stale desktop value must be cleared synchronously
  // or the panes flash one frame at 268px before the floor can apply.
  React.useLayoutEffect(() => {
    if (floating) return;
    document.documentElement.style.setProperty(
      '--nav-drawer-width',
      openedDrawer ? '268px' : '60px',
    );
  }, [floating, openedDrawer]);

  // Clear the inline override when the desktop rail unmounts (e.g. viewport
  // resized down to mobile) so a stale desktop width can't shadow the mobile 0.
  React.useLayoutEffect(
    () => () => {
      document.documentElement.style.removeProperty('--nav-drawer-width');
    },
    [],
  );

  return (
    <Drawer
      open={openedDrawer}
      className={classNames(
        css.drawer,
        floating && css.floating,
        openedDrawer ? css.open : css.close,
      )}
      variant="permanent"
      transitionDuration={100}
      anchor="left"
    >
      <div className={css.drawerInner}>
        <div className={css.topButtons}>
          {/* Header row (Claude-style): logo on the left, the expand/collapse
                control on the right. Collapses to a centered column on the rail. */}
          <div className={css.headerButtons}>
            {/* `data-nav-logo-lockup` is a cross-module query hook, not styled
                here: ChangelogDot brightens its faint read-state dot on hover of
                this whole lockup, and `css.logoContainer` is a hashed CSS-module
                name it can't address. Renaming the attribute breaks that hover
                silently — nothing in the build checks it. */}
            <div className={css.logoContainer} data-nav-logo-lockup>
              <Logo hideText={!openedDrawer} onClick={handleLogoClick} />
              {/* Sibling of the logo, not a child: it's absolutely positioned
                  against `.logoContainer`, and staying outside the logo's own
                  click area keeps its clicks from navigating to Workspace. */}
              <ChangelogDot />
            </div>
            <Tooltip title={openedDrawer ? 'Collapse' : 'Expand'} placement="right">
              <Button
                variant="nav-button"
                aria-label="toggle drawer"
                className={css.headerToggle}
                onClick={toggleDrawer}
              >
                <NavIcon>
                  <SidebarCollapseIcon />
                </NavIcon>
              </Button>
            </Tooltip>
          </div>
          <div className={classNames(css.topNavButtons, css.topNavButtonsScrollable)}>
            {!libraryManager.areLibraryStatsLoaded
              ? NAV_ITEMS.map((item) => (
                  <NavButtonSkeleton key={item.id} openedDrawer={openedDrawer} />
                ))
              : NAV_SECTIONS.map((section) => (
                  <div key={section.label ?? section.ids[0]} className={css.navSection}>
                    {section.label && (
                      <span
                        className={classNames(
                          css.sectionHeader,
                          !openedDrawer && css.sectionHeaderHidden,
                        )}
                        aria-hidden={!openedDrawer}
                      >
                        {section.label}
                      </span>
                    )}
                    {/* New Chat (primary) then Chats lead the flat top group —
                          both are chat-app actions, gated on the chat origin. */}
                    {section.showChatActions && CHAT_URL && (
                      <>
                        <NavButton
                          tooltip={!openedDrawer ? 'New Chat' : undefined}
                          label="New Chat"
                          activeWhenPathIs={false}
                          openedDrawer={openedDrawer}
                          icon={<AddIcon />}
                          onClick={() => {
                            navigateToNewChat();
                            onFloatingClose?.();
                          }}
                        />
                        <NavButton
                          tooltip={!openedDrawer ? 'Chats' : undefined}
                          label="Chats"
                          activeWhenPathIs={false}
                          openedDrawer={openedDrawer}
                          icon={<ChatBubbleOutlineRoundedIcon />}
                          onClick={() => {
                            navigateToChats();
                            onFloatingClose?.();
                          }}
                        />
                      </>
                    )}
                    {section.ids.map((id) => renderNavItem(NAV_ITEMS_BY_ID[id]))}
                    {/* Artifacts trails the shared items — a chat-app surface
                          like New Chat/Chats, so it shares their origin gate. */}
                    {section.showChatActions && CHAT_URL && (
                      <NavButton
                        tooltip={!openedDrawer ? 'Artifacts' : undefined}
                        label="Artifacts"
                        activeWhenPathIs={false}
                        openedDrawer={openedDrawer}
                        icon={<PermMediaOutlinedIcon />}
                        onClick={() => {
                          navigateToArtifacts();
                          onFloatingClose?.();
                        }}
                      />
                    )}
                    {section.trailingIds?.map((id) => renderNavItem(NAV_ITEMS_BY_ID[id]))}
                  </div>
                ))}
            <RecentChatsSection hidden={!openedDrawer} onNavigate={onFloatingClose} />
          </div>
        </div>

        <div className={css.bottomButtons}>
          <AccountFooter opened={openedDrawer} onNavigate={onFloatingClose} />
        </div>
      </div>
    </Drawer>
  );
});

const NavIcon = ({ children }: { children: React.ReactNode }) => {
  return <div className={css.navIcon}>{children}</div>;
};

const NavButtonSkeleton = ({ openedDrawer }: { openedDrawer: boolean }) => {
  return (
    <Button
      variant="nav-button"
      className={css.navButton}
      disabled
      sx={{
        opacity: 0.3,
        '& *': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
        },
      }}
    >
      <NavIcon>
        <div
          style={{
            width: 20,
            height: 20,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
          }}
        />
      </NavIcon>
      {openedDrawer && (
        <span className={css.navLabel}>
          <div
            style={{
              width: 60,
              height: 14,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 4,
            }}
          />
        </span>
      )}
    </Button>
  );
};

const NavButton = ({
  tooltip,
  label,
  badge,
  activeWhenPathIs,
  openedDrawer,
  icon,
  ...buttonProps
}: {
  tooltip?: string;
  label: string;
  badge?: React.ReactNode;
  activeWhenPathIs: boolean;
  openedDrawer: boolean;
  icon: React.ReactNode;
} & ButtonProps) => {
  return (
    <Tooltip title={tooltip} placement="right">
      <Button
        variant="nav-button"
        className={classNames(css.navButton, buttonProps.className)}
        color={activeWhenPathIs ? 'active' : undefined}
        {...buttonProps}
      >
        <NavIcon>{icon}</NavIcon>
        {/* Always render the label so the button's internal flex layout
         *  stays identical between open and closed; toggling via
         *  `visibility` avoids the one-frame reflow flash that the
         *  conditional-mount approach caused on expand. */}
        <span
          className={classNames(css.navLabel, !openedDrawer && css.navLabelHidden)}
          aria-hidden={!openedDrawer}
        >
          {label}
          {badge}
        </span>
      </Button>
    </Tooltip>
  );
};
